import {
  Button,
  Col,
  Container,
  Flex,
  Image,
  Quantity,
  Row,
  Shape,
  SubTitle,
  styled,
  useTheme,
} from '@teko/ds-desktop';
import { BottomActionBar } from '@teko/ecommerce/mobile';
import { getTrackingData } from '@teko/tracking';
import { useCartV2, useProductDetailState, useTenantConfig } from 'contexts';
import { getEnv } from 'helpers';
import { t } from 'i18n';
import { useBottomButtons } from 'modules/product-detail/hooks/useBottomButtons';
import { useMemo } from 'react';
import { MAX_WIDTH } from './DetailPage';

export const BottomButtons = () => {
  const { colors } = useTheme();
  const { contactLink, allowBuyNow, uomCodeToDisplayDecimalQuantity } =
    useTenantConfig();
  const { onBuy, onBuyNow } = useBottomButtons();
  const {
    product,
    price,
    isOutOfStock,
    actionReady,
    payloadAddToCart,
    setPayloadAddToCart,
  } = useProductDetailState();
  const { loading: cartLoading } = useCartV2();

  const isValidPrice = useMemo(() => {
    if (!product) return false;
    if (price) return (price.finalPrice as number) >= 0;
  }, [product, price]);
  const showContact =
    !isValidPrice || isOutOfStock || !product?.status?.sellable;
  const precision =
    product?.uomCode === uomCodeToDisplayDecimalQuantity ? 3 : 0;

  const selectedVariants = useMemo(() => {
    return product?.productOptions?.rows
      .map((variant) =>
        variant.options
          .filter((option) => option.selected)
          .map((option) => option.label),
      )
      .join(', ');
  }, [product?.productOptions?.rows]);

  return (
    <StyledBottomActionBar>
      <StyledContent maxWidth={MAX_WIDTH}>
        <Flex style={{ alignItems: 'center' }} justify="center" width="100%">
          <Row justify="start" width="50%">
            <Row
              style={{ minWidth: 0, width: '60%' }}
              wrap={false}
              align="middle"
            >
              <Col style={{ minWidth: 48 }}>
                <Image
                  width={48}
                  height={48}
                  src={product?.images?.[0]?.src || ''}
                  alt={product?.images?.[0]?.alt}
                />
              </Col>
              <Col style={{ padding: '0 8px', minWidth: 0 }}>
                <EllipsisText style={{ fontWeight: 'bold', fontSize: '15px' }}>
                  {product?.displayName}
                </EllipsisText>
                {!!product?.productOptions?.rows.length ? (
                  <EllipsisText style={{ color: colors.textSecondary }}>
                    <span>{t('Phân loại')}: </span>
                    <span>{selectedVariants}</span>
                  </EllipsisText>
                ) : null}
              </Col>
            </Row>
            <QuantityBlock>
              <Quantity
                className="quantity-selector"
                disableMinus={payloadAddToCart?.quantity === 1}
                quantity={payloadAddToCart?.quantity || 1}
                setQuantity={(value) => {
                  if (value !== payloadAddToCart?.quantity) {
                    setPayloadAddToCart?.((prev) => {
                      return {
                        ...prev,
                        quantity: value,
                      };
                    });
                  }
                }}
                onPlus={() => {
                  setPayloadAddToCart?.((prev) => {
                    return {
                      ...prev,
                      quantity: (prev.quantity || 1) + 1,
                    };
                  });
                }}
                onMinus={() => {
                  if ((payloadAddToCart?.quantity || 1) - 1 > 0) {
                    setPayloadAddToCart?.((prev) => {
                      return {
                        ...prev,
                        quantity: (prev.quantity || 1) - 1,
                      };
                    });
                  }
                }}
                min={Math.pow(10, -precision)}
                {...(getEnv('EPIC_ECOM_489') !== 'enabled'
                  ? { decimalSeparator: '.' }
                  : {})}
                precision={precision}
                blurOnEnter
              />
            </QuantityBlock>
          </Row>
          <Flex style={{ alignItems: 'center' }} justify="end" width="50%">
            {showContact ? (
              <Shape width="100%" padding={['s0', 's16']}>
                {!!contactLink ? (
                  <Button
                    variant="primary"
                    width="100%"
                    disabled={!actionReady}
                    borderRadius="m"
                    onClick={(): void => {
                      if (contactLink) window.open(contactLink);
                      return;
                    }}
                    style={
                      !actionReady
                        ? {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            border: '1px solid rgba(0, 0, 0, 0.15)',
                            color: 'rgba(0, 0, 0, 0.25)',
                          }
                        : {}
                    }
                    {...getTrackingData({
                      isTrackContent: true,
                      contentName: 'contactSale',
                      target: 'productDetail',
                      regionName: 'bottomProductDetail',
                      payload: JSON.stringify({
                        sku: product?.sku,
                        screenName: 'productDetail',
                      }),
                    })}
                  >
                    <SubTitle level={2}>{t('Liên hệ')}</SubTitle>
                  </Button>
                ) : (
                  <Button
                    disabled
                    block
                    style={{
                      backgroundColor: '#0000000A',
                      border: '1px solid #00000026',
                    }}
                    borderRadius="m"
                  >
                    <SubTitle level={2} weight="medium" color="#00000040">
                      {t('Hết hàng')}
                    </SubTitle>
                  </Button>
                )}
              </Shape>
            ) : (
              <PurchaseButtons
                style={{ maxWidth: allowBuyNow ? 'fit-content' : '100%' }}
              >
                <Shape
                  width="100%"
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: 'addToCart',
                    target: 'productDetail',
                    regionName: 'bottomProductDetail',
                    payload: JSON.stringify({
                      sku: product.sku,
                      screenName: 'productDetail',
                    }),
                  })}
                >
                  <Button
                    width={allowBuyNow ? '180px' : '100%'}
                    variant={allowBuyNow ? 'secondary' : 'primary'}
                    disabled={!actionReady || cartLoading}
                    borderRadius="m"
                    onClick={onBuy}
                  >
                    <SubTitle level={2} style={{ whiteSpace: 'nowrap' }}>
                      {t('Thêm vào giỏ hàng')}
                    </SubTitle>
                  </Button>
                </Shape>
                {allowBuyNow && (
                  <Shape
                    width="100%"
                    {...getTrackingData({
                      isTrackContent: true,
                      contentName: 'buyNow',
                      target: 'productDetail',
                      regionName: 'bottomProductDetail',
                      payload: JSON.stringify({
                        sku: product.sku,
                        screenName: 'productDetail',
                      }),
                    })}
                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                    marginLeft="s16"
                  >
                    <Button
                      width="180px"
                      variant="primary"
                      disabled={!actionReady || cartLoading}
                      borderRadius="m"
                      onClick={onBuyNow}
                    >
                      <SubTitle level={2}>{t('Mua ngay')}</SubTitle>
                    </Button>
                  </Shape>
                )}
              </PurchaseButtons>
            )}
          </Flex>
        </Flex>
      </StyledContent>
    </StyledBottomActionBar>
  );
};

const StyledBottomActionBar = styled(BottomActionBar)`
  position: sticky;
  display: block;
  padding-left: 0;
  padding-right: 0;
  padding-top: 8px;
  padding-bottom: 8px;
  box-shadow: 0 -5px 10px -3px rgba(0, 0, 0, 0.2);
`;

const StyledContent = styled(Container)`
  padding-left: 8%;
  padding-right: 8%;
`;

const EllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuantityBlock = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
  .quantity-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    margin-right: 8px;
    white-space: nowrap;
  }
  .quantity-selector {
    width: 132px !important;
    height: 36px !important;
    .rc-input-number-input {
      border-left: 1px solid #e4e5f0;
      border-right: 1px solid #e4e5f0;
      background-color: white;
    }
  }
`;
const PurchaseButtons = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  width: 100%;
`;
